import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Stack,
  Typography,
  IconButton,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DocumentCategoryIds, Insight } from "@stratify/shared";
import { createInsightStart, updateInsightStart } from "../store/insightsSlice";
import { selectInsightsLoading } from "../store/insightsSelectors";
import { selectUserId } from "../../auth/store/authSelectors";
import { trackEvent } from "../../analytics/store/analyticsSlice";

interface InsightFormProps {
  documentId: string;
  documentCategoryId: DocumentCategoryIds;
  insight?: Insight;
  onClose?: () => void;
}

export const InsightForm: React.FC<InsightFormProps> = ({
  documentId,
  documentCategoryId,
  insight,
  onClose,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectInsightsLoading);
  const userId = useSelector(selectUserId);

  const [description, setDescription] = useState(insight?.description || "");
  const [references, setReferences] = useState(
    insight?.references || [{ pageNumber: 1, text: "" }]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const filteredReferences = references.filter(
      (ref) => ref.text.trim() !== ""
    );

    if (insight?.id) {
      // Update existing insight
      dispatch(
        trackEvent({
          name: "insight_updated",
          params: {
            document_id: documentId,
            insight_id: insight.id,
          },
        })
      );
      dispatch(
        updateInsightStart({
          documentId,
          insightId: insight.id,
          updates: {
            description,
            references: filteredReferences,
          },
        })
      );
    } else {
      // Create new insight
      dispatch(
        trackEvent({
          name: "insight_created",
          params: {
            document_id: documentId,
          },
        })
      );
      if (!userId) {
        throw new Error("User must be authenticated to create insights");
      }

      dispatch(
        createInsightStart({
          documentId,
          userId,
          text: description,
          metadata: {
            documentCategoryId,
            references: filteredReferences,
            createdBy: "user",
          },
        })
      );
    }

    onClose?.();
  };

  const handleAddReference = () => {
    setReferences([...references, { pageNumber: 1, text: "" }]);
  };

  const handleRemoveReference = (index: number) => {
    setReferences(references.filter((_, i) => i !== index));
  };

  const handleReferenceChange = (
    index: number,
    field: "pageNumber" | "text",
    value: string | number
  ) => {
    const newReferences = [...references];
    newReferences[index] = {
      ...newReferences[index],
      [field]: value,
    };
    setReferences(newReferences);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={4}
          fullWidth
          required
        />

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            References (Optional)
          </Typography>
          <Stack spacing={2}>
            {references.map((reference, index) => (
              <Paper key={index} sx={{ p: 2 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    label="Page Number"
                    type="number"
                    value={reference.pageNumber}
                    onChange={(e) =>
                      handleReferenceChange(
                        index,
                        "pageNumber",
                        parseInt(e.target.value)
                      )
                    }
                    sx={{ width: "150px" }}
                  />
                  <TextField
                    label="Reference Text"
                    value={reference.text}
                    onChange={(e) =>
                      handleReferenceChange(index, "text", e.target.value)
                    }
                    multiline
                    rows={2}
                    fullWidth
                  />
                  <IconButton
                    onClick={() => handleRemoveReference(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Paper>
            ))}
            <Button variant="outlined" onClick={handleAddReference}>
              Add Reference
            </Button>
          </Stack>
        </Box>

        <Box display="flex" gap={2} justifyContent="flex-end">
          {onClose && (
            <Button onClick={onClose} variant="outlined" disabled={loading}>
              Cancel
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={loading}>
            {insight ? "Update" : "Create"} Insight
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
